/* eslint-disable react-hooks/exhaustive-deps */
import { type CredentialResponse, GoogleLogin } from '@react-oauth/google';
import clsx from 'clsx';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { type AuthModel, initialLoginRequestValues, type LoginRequest, loginSchema } from '../core/_models';
import { login, loginWithGoogle } from '../core/_requests';
import { useAuth } from '../core/Auth';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_APP_GOOGLE_CLIENT_ID as string;

export function Login() {
  const [loading, setLoading] = useState(false);
  const { saveAuth, errorMessage } = useAuth();

  const formik = useFormik({
    initialValues: initialLoginRequestValues,
    validationSchema: loginSchema,
    onSubmit: (values) => {
      setLoading(true);
      sendLoginRequest(values);
    },
  });

  useEffect(() => {
    if (errorMessage) {
      formik.setStatus(errorMessage);
      formik.setSubmitting(false);
      setLoading(false);
    }
  }, [errorMessage]);

  const handleGoogleLoginSuccess = ({ credential }: CredentialResponse) => {
    if (!credential) {
      formik.setStatus('Credenciais de login incorretas');
      formik.setSubmitting(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    sendGoogleLoginRequest(credential);
  };

  const handleGoogleLoginError = () => {
    formik.setStatus('Credenciais de login incorretas');
    formik.setSubmitting(false);
    setLoading(false);
  };

  const { mutate: sendLoginRequest } = useMutation({
    mutationFn: ({ email, password }: LoginRequest) => login(email, password),

    onSuccess: (response: { data: AuthModel }) => {
      saveAuth(response.data);
    },

    onError: (error: unknown) => {
      saveAuth(undefined);
      formik.setSubmitting(false);
      formik.setStatus(getErrorMessage(error));
    },

    onSettled: () => {
      setLoading(false);
    },
  });

  const { mutate: sendGoogleLoginRequest } = useMutation({
    mutationFn: async (credential: string) => {
      const response = await loginWithGoogle(credential);
      return response.data;
    },

    onSuccess: (response: AuthModel) => {
      saveAuth(response);
    },

    onError: (error: unknown) => {
      saveAuth(undefined);
      formik.setStatus(getErrorMessage(error));
      formik.setSubmitting(false);
    },

    onSettled: () => {
      setLoading(false);
    },
  });

  return (
    <form noValidate className="form w-100" id="kt_login_signin_form" onSubmit={formik.handleSubmit}>
      {!!GOOGLE_CLIENT_ID && (
        <>
          <div className="row g-3 mb-9 d-flex justify-content-center">
            {/* begin::Col */}
            <div className="col-md-12 d-flex justify-content-center align-items-center">
              <GoogleLogin
                locale="pt-BR"
                shape="rectangular"
                theme="outline"
                onError={handleGoogleLoginError}
                onSuccess={handleGoogleLoginSuccess}
              />
            </div>
            {/* end::Col */}
          </div>
          {/* begin::Separator */}
          <div className="separator separator-content my-10 border-gray-700">
            <span className="w-175px text-gray-500 fw-semibold fs-7 bg-transparent px-3">Ou com e-mail</span>
          </div>
          {/* end::Separator */}
        </>
      )}
      {/* end::Login options */}

      {!!formik.status && (
        <div className="mb-lg-15 alert alert-danger bg-danger bg-opacity-10 border-danger border-dashed">
          <div className="alert-text font-weight-bold text-danger">{formik.status}</div>
        </div>
      )}

      {/* begin::Form group */}
      <div className="fv-row mb-8">
        <label className="form-label fs-6 fw-semibold" htmlFor="login-email">
          E-mail corporativo
        </label>
        <input
          id="login-email"
          placeholder="E-mail"
          {...formik.getFieldProps('email')}
          autoComplete="username"
          className={clsx(
            'form-control',
            { 'is-invalid border-danger': formik.touched.email && formik.errors.email },
            {
              'is-valid border-success': formik.touched.email && !formik.errors.email,
            },
          )}
          name="email"
          type="email"
        />
        {!!formik.touched.email && !!formik.errors.email && (
          <div className="fv-plugins-message-container mt-2">
            <span className="text-danger fs-7" role="alert">
              {formik.errors.email}
            </span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className="fv-row mb-3">
        <div className="d-flex flex-stack mb-2">
          <label className="form-label fw-semibold fs-6 mb-0" htmlFor="login-password">
            Senha
          </label>
        </div>
        <input
          autoComplete="current-password"
          id="login-password"
          placeholder="Senha"
          type="password"
          {...formik.getFieldProps('password')}
          className={clsx(
            'form-control',
            {
              'is-invalid border-danger': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid border-success': formik.touched.password && !formik.errors.password,
            },
          )}
        />
        {!!formik.touched.password && !!formik.errors.password && (
          <div className="fv-plugins-message-container mt-2">
            <div className="fv-help-block">
              <span className="text-danger fs-7" role="alert">
                {formik.errors.password}
              </span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Wrapper */}
      <div className="d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8 mt-4">
        <div />

        {/* begin::Link */}
        <Link className="link-primary fs-7 fw-bold" to="/auth/esqueceu-senha">
          Esqueceu a senha?
        </Link>
        {/* end::Link */}
      </div>
      {/* end::Wrapper */}

      {/* begin::Action */}
      <div className="d-grid mb-10">
        <button
          className="btn btn-primary w-100"
          disabled={formik.isSubmitting || !formik.isValid}
          id="kt_sign_in_submit"
          type="submit"
        >
          {!loading && (
            <span className="indicator-label fs-5 d-flex align-items-center justify-content-center">Entrar</span>
          )}
          {!!loading && (
            <span className="indicator-progress fs-5" style={{ display: 'block' }}>
              Autenticando...
              <span className="spinner-border spinner-border-sm align-middle ms-2" />
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
    </form>
  );
}
